import React, { useState, useEffect, useCallback, useRef } from 'react';
import { errorMapping, makeRequest } from '../../../assets/functions_helper';
import ListData from '../../../components/listData';
import {
  Box,
  DialogContentText,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SnackBar from '../../../components/snackBar';
import QRCodeStyling from 'qr-code-styling';
import { CustomDialog, CustomDialogActions, CustomDialogContent, CustomDialogTitle } from '../../../components/customDialog';
import { NoSuggestionsTextField } from '../../../components/textFields';
import { CloseButton, SendButton } from '../../../components/buttons';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
function ListTablesRestaurant({
  idTable,
  title,
  titleCreate,
  titleContent,
  nameColumn1,
  nameColumn3,
  nameOfRestaurant,
}) {
  const [allTablesRaw, setAllTablesRaw] = useState([]);
  const [tablesRestaurant, setTablesRestaurant] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [onlyReservable, setOnlyReservable] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [tableDetail, setTableDetail] = useState({
    id: null,
    name: '',
    seat_count: '',
    can_be_reserved: false,
    isActive: true,
  });

  const [url, setUrl] = useState('');
  const [qr, setQr] = useState(false);
  const navigate = useNavigate();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [tableToDelete, setTableToDelete] = useState(null); // { id, name }
  const [openTableDelete, setOpenTableDelete] = useState(false);

  // QR custom styles loaded from backend
  const [qrStyles, setQrStyles] = useState(null);
  const [loadingQrStyles, setLoadingQrStyles] = useState(false);
  // PNG data URL generated from QRCodeStyling
  const [qrImageUrl, setQrImageUrl] = useState(null);
  // Ref al contenedor QR+logo para captura con html-to-image
  const qrContainerRef = useRef(null);

  // We define local variable url to prevent collision with state url
  useEffect(() => {
    let requestUrl = `restaurants/${idTable}/tables`;

    makeRequest(
      'GET',
      requestUrl,
      {},
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => {
        navigate('/login');
      }
    ).then((response) => {
      setAllTablesRaw(response || []);
      setIsLoaded(true);
    });
  }, [idTable, updateTrigger, navigate]);

  useEffect(() => {
    let filtered = allTablesRaw;
    if (onlyReservable) {
      filtered = filtered.filter(t => t.can_be_reserved === true);
    }

    const updatedTables = filtered.map((table) => ({
      ...table,
      id: table.public_id,
      realId: table.id,
      title: table.name,
      seat_count_display: table.seat_count
        ? <Chip label={`Capacidad: ${table.seat_count}`} size="small" variant="outlined" />
        : <Chip label="Capacidad: —" size="small" variant="outlined" sx={{ color: '#94a3b8', borderColor: '#e2e8f0' }} />,
      reservation_badge: table.can_be_reserved
        ? <Chip label="Reservable 📅" size="small" color="primary" sx={{ fontWeight: 600 }} />
        : <Chip label="No reservable" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b' }} />,
      title2: table.is_active === false 
        ? <Chip label="Mesa Inactiva" size="small" color="error" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />
        : <Chip label="Mesa Activa" size="small" color="success" variant="outlined" sx={{ ml: 1, fontWeight: 600 }} />,
    }));

    setTablesRestaurant(updatedTables);
  }, [allTablesRaw, onlyReservable]);

  const handleClickCreateUser = () => {
    const isEditing = isEdit;
    const endpoint = localStorage.getItem('rol') === '2'
      ? (isEditing ? `restaurants/update_table/${tableDetail.realId}` : 'restaurant_admin/create_table')
      : (isEditing ? `restaurants/update_table/${tableDetail.realId}` : 'restaurants/create_table');

    const method = isEditing ? 'PUT' : 'POST';

    // Parsear seat_count solo si tiene un valor
    const parsedSeatCount = tableDetail.seat_count ? parseInt(tableDetail.seat_count, 10) : null;

    makeRequest(
      method,
      endpoint,
      {
        restaurant_id: idTable,
        name: tableDetail.name,
        seat_count: parsedSeatCount,
        can_be_reserved: tableDetail.can_be_reserved,
        is_active: tableDetail.isActive
      },
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => {
        navigate('/login');
      }
    )
      .then(() => {
        setOpen(false);
        setUpdateTrigger((prev) => prev + 1);
      })
      .catch((err) => {
        for (const error in errorMapping) {
          if (error.includes(err['error_code'])) {
            setSnackBarMessage(errorMapping[err['error_code']]);
            setSnackBarOpen(true);
            return;
          }
        }
      });
  };

  // Load QR styles from backend when the QR dialog opens
  const loadQrStylesAndOpen = useCallback((tableUrl) => {
    setUrl(tableUrl);
    setQrStyles(null);
    setQrImageUrl(null);
    setLoadingQrStyles(true);
    makeRequest(
      'GET',
      `restaurant_admin/qr-styles/${idTable}`,
      null,
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
    )
      .then((data) => {
        setQrStyles(data?.styles || {});
      })
      .catch(() => {
        setQrStyles({});
      })
      .finally(() => {
        setLoadingQrStyles(false);
        setQr(true);
      });
  }, [idTable]);

  // Generate QR as PNG blob → data URL (avoids ref/DOM timing issues)
  useEffect(() => {
    if (!qr || !url || loadingQrStyles) return;

    setQrImageUrl(null);
    const s = qrStyles || {};
    const instance = new QRCodeStyling({
      width: 300,
      height: 300,
      type: 'png',
      data: url,
      margin: 8,
      dotsOptions:        { color: s.dot_color    || '#0f172a', type: s.dot_style    || 'rounded' },
      backgroundOptions:  { color: s.bg_color     || '#ffffff' },
      cornersSquareOptions: { type: s.corner_style || 'square', color: s.corner_color || '#f73541' },
      cornersDotOptions:  { type: 'dot',            color: s.corner_color || '#f73541' },
    });

    instance.getRawData('png').then((blob) => {
      // Convertir a base64 data URI para que html-to-image no intente hacer fetch de un blob URL
      const reader = new FileReader();
      reader.onloadend = () => setQrImageUrl(reader.result);
      reader.readAsDataURL(blob);
    });
  }, [qr, url, qrStyles, loadingQrStyles]);

  // Download: Super simplificado, ya que QRCodeStyling tiene los estilos incrustados en qrImageUrl
  const handleDownloadQR = useCallback(async () => {
    if (!qrImageUrl) return;

    const filename = `qr-mesa-${idTable}.png`;

    const a = document.createElement('a');
    a.download = filename;
    a.href = qrImageUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [qrImageUrl, idTable]);

  const deleteTable = (table) => {
    const url =
      localStorage.getItem('rol') === '2'
        ? `restaurant_admin/delete_table/${table.id}`
        : `restaurants/delete_table/${table.id}`;

    makeRequest(
      'DELETE',
      url,
      { restaurant_id: idTable },  // Enviar el ID del restaurante en el body
      'application/json',
      localStorage.getItem('token'),
      localStorage.getItem('tokenType'),
      null,
      null,
      () => {
        navigate('/login');
      }
    )
      .then(() => {
        // Disparar evento global para que la vista de "Agenda" u otras vistas escuchen y se recarguen
        window.dispatchEvent(new CustomEvent('tableDeleted', { detail: { tableId: table.id } }));

        setSnackBarMessage('Mesa eliminada correctamente');
        setSnackBarOpen(true);
        setUpdateTrigger((prev) => prev + 1);
        setOpenTableDelete(false);
        setTableToDelete(null);
      })
      .catch((err) => {
        for (const error in errorMapping) {
          if (error.includes(err['error_code'])) {
            setSnackBarMessage(errorMapping[err['error_code']]);
            setSnackBarOpen(true);
            return;
          }
        }
      });
  };

  return (
    <>
      {isLoaded ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, p: 0.5, bgcolor: '#f1f5f9', borderRadius: '12px', width: 'fit-content', ml: { xs: 1, md: 3 }, mt: 2, mb: 1 }}>
            <Box 
              onClick={() => setOnlyReservable(false)}
              sx={{ px: { xs: 2, md: 3 }, py: 1.2, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', bgcolor: !onlyReservable ? '#fff' : 'transparent', color: !onlyReservable ? '#0f172a' : '#64748b', boxShadow: !onlyReservable ? '0 2px 8px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}
            >
              Todas las Mesas
            </Box>
            <Box 
              onClick={() => setOnlyReservable(true)}
              sx={{ px: { xs: 2, md: 3 }, py: 1.2, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', bgcolor: onlyReservable ? '#fff' : 'transparent', color: onlyReservable ? '#3b82f6' : '#64748b', boxShadow: onlyReservable ? '0 2px 8px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              📅 Reservables
            </Box>
          </Box>
          <ListData
            title={title}
            data={tablesRestaurant}
            setData={setTablesRestaurant}
            edit={false} // Deshabilitamos la edición nativa para usar extraActions
            dynamicColumns={[
              { header: "Capacidad", dataKey: "seat_count_display" },
              { header: "Estado Reservas", dataKey: "reservation_badge" }
            ]}
            extraActions={[
              {
                icon: <QrCode2RoundedIcon fontSize="small" />,
                tooltip: "Ver Código QR",
                onClick: (item) => {
                  const baseUrl = window.location.origin;
                  loadQrStylesAndOpen(`${baseUrl}/restaurantMenu/${item.public_id || item.id}`);
                }
              },
              {
                icon: <EditRoundedIcon fontSize="small" />,
                tooltip: "Editar Mesa",
                onClick: (item) => {
                  setIsEdit(true);
                  setTableDetail({
                    id: item.public_id || item.id,
                    realId: item.realId,
                    name: item.title,
                    seat_count: item.seat_count || '',
                    can_be_reserved: item.can_be_reserved || false,
                    isActive: item.is_active !== false // Por defecto true a menos que sea explicitamente false
                  });
                  setOpen(true);
                }
              }
            ]}
            page={null}
            setPage={null}
            changePage={() => { }}
            pages={null}
            total={null}
            currentPage={null}
            create={true}
            createMessage="Nueva Mesa"
            createFunction={() => {
              setIsEdit(false);
              setTableDetail({ name: '', seat_count: '', can_be_reserved: false, isActive: true, id: null });
              setOpen(true);
            }}
            pagination={false}
            nameColumn1={'Mesas'}
            nameColumn3={'Acciones'}
            nameOfRestaurant={nameOfRestaurant}
            qr_option={false}
            deleted={true}
            deletedFunction={(id, title) => {
              setOpenTableDelete(true);
              setTableToDelete({ id, name: title });
            }}
            deteletedMessage="Eliminar Mesa"
          />

          {/* Diálogo para crear / editar mesa */}
          <CustomDialog open={open} onClose={() => setOpen(!open)}>
            <CustomDialogTitle>{isEdit ? "Editar Mesa" : titleCreate}</CustomDialogTitle>
            <CustomDialogContent>
              <DialogContentText>{isEdit ? "Modifica los valores de la mesa." : titleContent}</DialogContentText>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <NoSuggestionsTextField
                  autoFocus
                  required
                  id="name"
                  name="name"
                  label="Nombre de mesa (Ej: Mesa 1, Terraza A)"
                  type="text"
                  fullWidth
                  value={tableDetail.name}
                  onChange={(e) =>
                    setTableDetail({ ...tableDetail, name: e.target.value })
                  }
                />

                <NoSuggestionsTextField
                  id="seat_count"
                  name="seat_count"
                  label="Capacidad de personas (Opcional)"
                  type="number"
                  inputProps={{ min: 1 }}
                  fullWidth
                  value={tableDetail.seat_count}
                  onChange={(e) =>
                    setTableDetail({ ...tableDetail, seat_count: e.target.value })
                  }
                  helperText="Deja vacío si la capacidad es desconocida o irrelevante."
                />

                <Box sx={{ mt: 1, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tableDetail.can_be_reserved}
                        onChange={(e) => setTableDetail({ ...tableDetail, can_be_reserved: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={<Box sx={{ fontWeight: 600 }}>Permitir reservas públicas</Box>}
                  />
                  <DialogContentText sx={{ fontSize: '0.8rem', mt: 1 }}>
                    Al activar esta opción, la mesa aparecerá como disponible en el formulario de reservas online para los clientes.
                  </DialogContentText>
                </Box>

                <Box sx={{ mt: 1, p: 2, bgcolor: tableDetail.isActive ? '#f0fdf4' : '#fef2f2', border: '1px solid', borderColor: tableDetail.isActive ? '#bbf7d0' : '#fecaca', borderRadius: '12px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tableDetail.isActive}
                        onChange={(e) => setTableDetail({ ...tableDetail, isActive: e.target.checked })}
                        color={tableDetail.isActive ? "success" : "error"}
                      />
                    }
                    label={<Box sx={{ fontWeight: 600, color: tableDetail.isActive ? '#166534' : '#991b1b' }}>{tableDetail.isActive ? "Mesa Activa (Visible en sistema)" : "Mesa Inactiva (Oculta temporamente)"}</Box>}
                  />
                  <DialogContentText sx={{ fontSize: '0.8rem', mt: 1, color: tableDetail.isActive ? '#166534' : '#991b1b' }}>
                    Modifica este control si la mesa está temporalmente fuera de servicio, dañada o no quieres que el personal la utilice o asigne.
                  </DialogContentText>
                </Box>
              </Box>

            </CustomDialogContent>
            <CustomDialogActions>
              <CloseButton onClick={() => setOpen(!open)}>Cerrar</CloseButton>
              <SendButton onClick={handleClickCreateUser}>{isEdit ? "Guardar Cambios" : "Crear Mesa"}</SendButton>
            </CustomDialogActions>
          </CustomDialog>

          {/* Diálogo para mostrar QR con estilos personalizados */}
          <CustomDialog open={qr} onClose={() => setQr(false)}>
            <CustomDialogTitle>Código QR de Mesa</CustomDialogTitle>
            <CustomDialogContent>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 1,
              }}>
                {loadingQrStyles ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#f73541' }} />
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Cargando estilos del QR...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Contenedor del QR estilizado */}
                    <Box
                      ref={qrContainerRef}
                      sx={{
                      background: `
                        linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                        linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                        linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                      `,
                      backgroundSize: '20px 20px',
                      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '20px',
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}>
                      {/* QR como imagen PNG — ya incluye el logo y estilos */}
                      {qrImageUrl
                        ? <img src={qrImageUrl} alt="QR Code" style={{ width: 260, height: 260, borderRadius: 14, display: 'block' }} />
                        : <Box sx={{ width: 260, height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={36} sx={{ color: '#f73541' }} /></Box>
                      }
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <DialogContentText sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.5, fontWeight: 500 }}>
                        Escanea este código con tu cámara para acceder al menú
                      </DialogContentText>
                      {qrStyles && Object.keys(qrStyles).length > 0 && (
                        <Typography variant="caption" sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          mt: 0.5, color: '#f73541', fontWeight: 700,
                        }}>
                          ✨ Estilo personalizado aplicado
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </CustomDialogContent>
            <CustomDialogActions>
              <SendButton
                onClick={handleDownloadQR}
                disabled={loadingQrStyles || !qrImageUrl}
                startIcon={<DownloadRoundedIcon />}
              >
                Descargar QR
              </SendButton>
              <CloseButton onClick={() => setQr(false)}>Cerrar</CloseButton>
            </CustomDialogActions>
          </CustomDialog>

          {/* Diálogo de confirmación para eliminar mesa */}
          <CustomDialog open={openTableDelete} onClose={() => setOpenTableDelete(false)}>
            <CustomDialogTitle>Eliminar Mesa</CustomDialogTitle>
            <CustomDialogContent>
              ¿Está seguro que desea eliminar la mesa <strong>{tableToDelete?.name}</strong>?
            </CustomDialogContent>
            <CustomDialogActions>
              <CloseButton onClick={() => deleteTable(tableToDelete)}>Eliminar</CloseButton>
              <CloseButton
                onClick={() => setOpenTableDelete(false)}
                sx={{
                  backgroundColor: '#E0E0E0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#D5D5D5',
                  },
                }}
              >
                Cancelar
              </CloseButton>
            </CustomDialogActions>
          </CustomDialog>

          {snackBarOpen && (
            <SnackBar
              openSnackBar={snackBarOpen}
              setOpenSnackBar={setSnackBarOpen}
              messageSnackBar={snackBarMessage}
            />
          )}
        </Box>
      ) : (
        <Box sx={{ height: '100vh' }} />
      )}
    </>
  );
}

export default ListTablesRestaurant;